import { fetchAuthSession } from 'aws-amplify/auth';
import toast from 'react-hot-toast';

const API_ENDPOINT =
  process.env.REACT_APP_API_ENDPOINT ||
  'https://jeabwk7au0.execute-api.eu-west-2.amazonaws.com/braingrid';

async function getAuthHeaders() {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    if (token) {
      return { Authorization: token };
    }
  } catch {
    // Not authenticated
  }
  return {};
}

async function handleResponse(res) {
  if (res.ok) {
    if (res.status === 204) return null;
    const text = await res.text();
    return text ? JSON.parse(text) : null;
  }

  let data = null;
  try {
    data = await res.json();
  } catch {
    // Non-JSON error body
  }
  const message = data?.error || res.statusText || 'Request failed';

  if (res.status === 401) {
    window.location.href = '/login';
  } else {
    toast.error(message);
  }

  const err = new Error(message);
  err.status = res.status;
  err.data = data;
  throw err;
}

export async function apiFetch(path, options = {}) {
  const authHeaders = await getAuthHeaders();
  const headers = {
    ...(options.body ? { 'Content-Type': 'application/json' } : {}),
    ...authHeaders,
    ...(options.headers || {}),
  };
  return fetch(`${API_ENDPOINT}${path}`, { ...options, headers });
}

// Silent background operations (launch-time entitlement sync, daily-seed
// fetch, guest telemetry) — no toast, no 401 login redirect, returns null on
// any failure. See shared-docs/error-surfacing.md for the decision rule.
export async function silentFetch(path, method = 'GET', body = null) {
  try {
    const authHeaders = await getAuthHeaders();
    const options = {
      method,
      headers: { 'Content-Type': 'application/json', ...authHeaders },
    };
    if (body) options.body = JSON.stringify(body);
    const res = await fetch(`${API_ENDPOINT}${path}`, options);
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

async function apiGet(path) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_ENDPOINT}${path}`, { headers });
  return handleResponse(res);
}

async function apiPost(path, body) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_ENDPOINT}${path}`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  });
  return handleResponse(res);
}

async function apiPut(path, body) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_ENDPOINT}${path}`, {
    method: 'PUT',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  });
  return handleResponse(res);
}

async function apiDelete(path) {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_ENDPOINT}${path}`, {
    method: 'DELETE',
    headers,
  });
  return handleResponse(res);
}

// ─── Public API ─────────────────────────────────────────────────

export async function submitScore({ score, linesCleared, bestCombo, moves, duration }) {
  return apiPost('/scores', { score, linesCleared, bestCombo, moves, duration });
}

export async function getLeaderboard(limit = 25) {
  return apiGet(`/leaderboard?limit=${limit}`);
}

export async function getDailyChallenge() {
  // Silent: fires on daily-mode load for guests and signed-in users alike.
  // PlayScreen has a local same-hash fallback seed, so a failure here must
  // never toast or bounce a guest to /login.
  return silentFetch('/daily');
}

export async function submitDailyScore({ score, linesCleared }) {
  return apiPost('/daily/scores', { score, linesCleared });
}

export async function getDailyLeaderboard() {
  return apiGet('/daily/leaderboard');
}

export async function getProfile() {
  return apiGet('/profile');
}

export async function updateProfile(displayName) {
  return apiPut('/profile', { displayName });
}

export async function deleteAccount() {
  return apiDelete('/user/account');
}

// ─── Payment & Entitlement API ──────────────────────────────────

export async function getEntitlements() {
  return apiGet('/payments/entitlements');
}

export async function verifyApplePurchase(txData) {
  return apiPost('/payments/verify-apple', txData);
}

export async function restoreApple() {
  return apiPost('/payments/restore-apple', {});
}

export async function getProducts() {
  return apiGet('/payments/products');
}

export async function useConsumable(type) {
  return apiPost('/payments/consume', { type });
}

export async function getInventory() {
  return apiGet('/payments/inventory');
}
